/**
 * HolidayHoursManager — full Holiday Hours / Special Dates CRUD UI.
 *
 * Used in both:
 *   - /onboarding/setup (after Business Hours section)
 *   - /dashboard → Settings tab (planned)
 *
 * Features:
 *   #1  Add Special Date modal (date picker + title + status type)
 *   #2  Status types: Open Normally / Closed All Day / Custom Hours
 *   #3  Custom hours (open/close time pickers)
 *   #4  Optional Special Note
 *   #5  Unlimited special dates
 *   #6  Edit · Delete · Duplicate
 *   #11 Quick presets (8 US holidays — auto-fills date + title)
 *   #12 Seasonal date-range mode
 *
 * Wired against:
 *   GET    /api/restaurants/{id}/special-hours
 *   POST   /api/restaurants/{id}/special-hours
 *   PATCH  /api/restaurants/{id}/special-hours/{sh_id}
 *   DELETE /api/restaurants/{id}/special-hours/{sh_id}
 *   POST   /api/restaurants/{id}/special-hours/{sh_id}/duplicate
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarDays,
  Plus,
  Pencil,
  Copy,
  Trash2,
  X,
  Check,
  Loader2,
  Clock,
  Snowflake,
  Sun,
  Sparkles,
  PartyPopper,
  Flame,
  PauseCircle,
  AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// FEATURE #11 — US holiday presets. Years computed dynamically (current
// year if upcoming, else next year). Title + ISO date pre-filled.
const buildPresets = () => {
  const y = new Date().getFullYear();
  const next = (m, d) => {
    const today = new Date();
    const candidate = new Date(y, m - 1, d);
    const target = candidate < today
      ? new Date(y + 1, m - 1, d)
      : candidate;
    return target.toISOString().slice(0, 10);
  };
  return [
    { title: 'Christmas Eve',     date: next(12, 24), Icon: Sparkles,  category: 'holiday' },
    { title: 'Christmas Day',     date: next(12, 25), Icon: Snowflake, category: 'holiday' },
    { title: 'New Year\'s Eve',   date: next(12, 31), Icon: PartyPopper, category: 'holiday' },
    { title: 'New Year\'s Day',   date: next(1, 1),   Icon: Sparkles,  category: 'holiday' },
    { title: 'Thanksgiving',      date: next(11, 27), Icon: Flame,     category: 'holiday' },
    { title: 'Memorial Day',      date: next(5, 26),  Icon: Sun,       category: 'holiday' },
    { title: 'Independence Day',  date: next(7, 4),   Icon: Sun,       category: 'holiday' },
    { title: 'Labor Day',         date: next(9, 1),   Icon: Sun,       category: 'holiday' },
  ];
};

const CATEGORY_OPTIONS = [
  { value: 'holiday',  label: 'Holiday',         Icon: CalendarDays },
  { value: 'seasonal', label: 'Seasonal',        Icon: Snowflake },
  { value: 'event',    label: 'Special Event',   Icon: PartyPopper },
  { value: 'closure',  label: 'Temporary Closure', Icon: PauseCircle },
  { value: 'custom',   label: 'Custom',          Icon: Sparkles },
];

const STATUS_OPTIONS = [
  { value: 'open_normally', label: 'Open Normally',  tone: 'emerald' },
  { value: 'closed',        label: 'Closed All Day', tone: 'rose' },
  { value: 'custom_hours',  label: 'Custom Hours',   tone: 'gold' },
];

const emptyForm = () => ({
  kind: 'single_day',
  title: '',
  status_type: 'open_normally',
  date: '',
  start_date: '',
  end_date: '',
  open_time: '11:00',
  close_time: '22:00',
  note: '',
  category: 'holiday',
});

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const HolidayHoursManager = ({ token, restaurantId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // null | {... existing} | 'new'
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const presets = useMemo(() => buildPresets(), []);

  const refresh = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/api/restaurants/${restaurantId}/special-hours`);
      setItems(r.data.special_hours || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load special hours.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew = (preset) => {
    setForm({
      ...emptyForm(),
      ...(preset ? { title: preset.title, date: preset.date, category: preset.category, status_type: 'closed' } : {}),
    });
    setEditing('new');
  };

  const openEdit = (it) => {
    setForm({
      kind: it.kind || 'single_day',
      title: it.title || '',
      status_type: it.status_type || 'open_normally',
      date: it.date || '',
      start_date: it.start_date || '',
      end_date: it.end_date || '',
      open_time: it.open_time || '11:00',
      close_time: it.close_time || '22:00',
      note: it.note || '',
      category: it.category || 'holiday',
    });
    setEditing(it);
  };

  const close = () => { setEditing(null); setForm(emptyForm()); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token || !restaurantId) return;
    setSaving(true);
    setError('');
    const payload = {
      kind: form.kind,
      title: form.title.trim(),
      status_type: form.status_type,
      category: form.category,
      note: form.note || null,
    };
    if (form.kind === 'single_day') payload.date = form.date;
    else {
      payload.start_date = form.start_date;
      payload.end_date = form.end_date;
    }
    if (form.status_type === 'custom_hours') {
      payload.open_time = form.open_time;
      payload.close_time = form.close_time;
    }
    try {
      if (editing === 'new') {
        await axios.post(`${API}/api/restaurants/${restaurantId}/special-hours`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.patch(`${API}/api/restaurants/${restaurantId}/special-hours/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      close();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save the special date.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try {
      await axios.delete(`${API}/api/restaurants/${restaurantId}/special-hours/${it.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete.');
    }
  };

  const onDuplicate = async (it) => {
    try {
      await axios.post(`${API}/api/restaurants/${restaurantId}/special-hours/${it.id}/duplicate`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not duplicate.');
    }
  };

  return (
    <div data-testid="holiday-hours-manager" className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[18px] font-bold text-white tracking-tight">
            Holiday Hours &amp; Special Dates
          </h3>
          <p className="mt-1 text-[12.5px] text-white/55 leading-relaxed max-w-2xl">
            Manage special schedules for holidays, events, temporary closures, and seasonal hours.
            Special dates always override your regular business hours.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openNew(null)}
          data-testid="holiday-hours-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13.5px] tracking-tight transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Special Date
        </button>
      </div>

      {error && (
        <div data-testid="holiday-hours-error" className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[12.5px] flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      {/* FEATURE #11 — Quick preset chips */}
      <div data-testid="holiday-hours-presets">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-2">Quick presets</p>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.title}
              type="button"
              onClick={() => openNew(p)}
              data-testid={`holiday-preset-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-yellow-500/25 bg-yellow-500/[0.04] hover:border-yellow-500/55 hover:bg-yellow-500/[0.10] text-yellow-200 text-[11.5px] font-semibold transition-all"
            >
              <p.Icon className="w-3 h-3" />
              {p.title}
              <span className="text-yellow-300/55 text-[10px]">· {formatDate(p.date).slice(0, 6)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div data-testid="holiday-hours-loading" className="h-24 rounded-xl border border-white/8 bg-white/[0.02] animate-pulse" />
      ) : items.length === 0 ? (
        <div className="p-6 rounded-xl border border-dashed border-white/12 text-center" data-testid="holiday-hours-empty">
          <CalendarDays className="w-6 h-6 text-white/35 mx-auto mb-2" />
          <p className="text-[13px] text-white/55">
            No special dates yet. Add one to override your regular hours on holidays or events.
          </p>
        </div>
      ) : (
        <ul data-testid="holiday-hours-list" className="space-y-2">
          {items.map((it) => {
            const cat = CATEGORY_OPTIONS.find((c) => c.value === it.category) || CATEGORY_OPTIONS[0];
            const status = STATUS_OPTIONS.find((s) => s.value === it.status_type) || STATUS_OPTIONS[0];
            const dateLabel = it.kind === 'date_range'
              ? `${formatDate(it.start_date)} – ${formatDate(it.end_date)}`
              : formatDate(it.date);
            return (
              <li
                key={it.id}
                data-testid={`holiday-hours-item-${it.id}`}
                className="p-3.5 rounded-xl border border-white/8 bg-white/[0.025] hover:border-white/15 transition-all flex items-start gap-3"
              >
                <span className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${
                  status.tone === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' :
                  status.tone === 'rose' ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' :
                  'border-yellow-500/30 bg-yellow-500/10 text-yellow-300'
                }`}>
                  <cat.Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[13.5px] font-bold text-white">{it.title}</p>
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded ${
                      status.tone === 'emerald' ? 'text-emerald-300 bg-emerald-500/10' :
                      status.tone === 'rose' ? 'text-rose-300 bg-rose-500/10' :
                      'text-yellow-300 bg-yellow-500/10'
                    }`}>
                      {status.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold">{cat.label}</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-white/55 flex items-center gap-1.5">
                    <CalendarDays className="w-3 h-3" />
                    {dateLabel}
                    {it.status_type === 'custom_hours' && it.open_time && (
                      <>
                        <Clock className="w-3 h-3 ml-1" />
                        {it.open_time} – {it.close_time}
                      </>
                    )}
                  </p>
                  {it.note && (
                    <p className="mt-1 text-[11.5px] text-white/45 italic">“{it.note}”</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(it)}
                    data-testid={`holiday-hours-edit-${it.id}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-white/8 hover:border-white/25 text-white/70 hover:text-white"
                    title="Edit"
                  ><Pencil className="w-3.5 h-3.5" /></button>
                  <button
                    type="button"
                    onClick={() => onDuplicate(it)}
                    data-testid={`holiday-hours-duplicate-${it.id}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-white/8 hover:border-white/25 text-white/70 hover:text-white"
                    title="Duplicate"
                  ><Copy className="w-3.5 h-3.5" /></button>
                  <button
                    type="button"
                    onClick={() => onDelete(it)}
                    data-testid={`holiday-hours-delete-${it.id}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-rose-500/20 bg-rose-500/[0.04] hover:border-rose-500/45 text-rose-300 hover:text-rose-200"
                    title="Delete"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal */}
      {editing && (
        <div
          data-testid="holiday-hours-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl border border-yellow-500/25 bg-gradient-to-br from-zinc-950 to-black p-5 sm:p-6 space-y-4"
          >
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-[17px] font-bold text-white">
                {editing === 'new' ? 'Add Special Date' : 'Edit Special Date'}
              </h4>
              <button
                type="button"
                onClick={close}
                data-testid="holiday-hours-modal-close"
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-white/10 hover:border-white/25 text-white/70 hover:text-white"
              ><X className="w-4 h-4" /></button>
            </div>

            {/* Single day / Date range toggle (FEATURE #12) */}
            <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
              {['single_day', 'date_range'].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, kind: k })}
                  data-testid={`holiday-hours-kind-${k}`}
                  className={`px-3 py-1.5 rounded-md text-[11.5px] font-bold transition-all ${
                    form.kind === k
                      ? 'bg-yellow-500/15 text-yellow-200 border border-yellow-500/40'
                      : 'text-white/65 hover:text-white'
                  }`}
                >
                  {k === 'single_day' ? 'Single date' : 'Date range (seasonal)'}
                </button>
              ))}
            </div>

            {/* Title */}
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Special Event Name</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                placeholder="e.g. Christmas Eve, Summer Schedule"
                data-testid="holiday-hours-input-title"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
              />
            </label>

            {/* Dates */}
            {form.kind === 'single_day' ? (
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Date</span>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  data-testid="holiday-hours-input-date"
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
                />
              </label>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Start date</span>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                    data-testid="holiday-hours-input-start"
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
                  />
                </label>
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">End date</span>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    required
                    data-testid="holiday-hours-input-end"
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
                  />
                </label>
              </div>
            )}

            {/* Category */}
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Category</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="holiday-hours-input-category"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>

            {/* Status type radios */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Status</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <label
                    key={s.value}
                    data-testid={`holiday-hours-status-${s.value}`}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer text-[12.5px] font-bold transition-all ${
                      form.status_type === s.value
                        ? s.tone === 'emerald' ? 'border-emerald-500/45 bg-emerald-500/[0.08] text-emerald-200' :
                          s.tone === 'rose' ? 'border-rose-500/45 bg-rose-500/[0.08] text-rose-200' :
                          'border-yellow-500/45 bg-yellow-500/[0.08] text-yellow-200'
                        : 'border-white/10 bg-white/[0.02] text-white/75 hover:border-white/25'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status_type"
                      value={s.value}
                      checked={form.status_type === s.value}
                      onChange={() => setForm({ ...form, status_type: s.value })}
                      className="sr-only"
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Custom hours */}
            {form.status_type === 'custom_hours' && (
              <div className="grid grid-cols-2 gap-3" data-testid="holiday-hours-custom-hours-row">
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Open time</span>
                  <input
                    type="time"
                    value={form.open_time}
                    onChange={(e) => setForm({ ...form, open_time: e.target.value })}
                    required
                    data-testid="holiday-hours-input-open"
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
                  />
                </label>
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Close time</span>
                  <input
                    type="time"
                    value={form.close_time}
                    onChange={(e) => setForm({ ...form, close_time: e.target.value })}
                    required
                    data-testid="holiday-hours-input-close"
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
                  />
                </label>
              </div>
            )}

            {/* Note */}
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Note (optional)</span>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                maxLength={140}
                placeholder="e.g. Closing early for Christmas Eve"
                data-testid="holiday-hours-input-note"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-yellow-500 rounded-xl px-3 py-2.5 text-[13.5px] text-white outline-none"
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/8">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2.5 rounded-xl border border-white/10 hover:border-white/25 text-white/75 hover:text-white font-bold text-[13px]"
              >Cancel</button>
              <button
                type="submit"
                disabled={saving}
                data-testid="holiday-hours-save-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 disabled:opacity-50 text-black font-extrabold text-[13.5px] transition-all"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {editing === 'new' ? 'Add date' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default HolidayHoursManager;
